import type {FriendData, SteamID64, Friend} from '../types/steam';

// CACHE_VERSION: Increment this whenever the cache schema or usage changes.
// This will automatically clear the cache for all users to prevent compatibility issues.
// Current version: 3 (added caching for friends lists in getFriendsList)
const CACHE_VERSION: number = 3;
const CACHE_VERSION_KEY: string = 'steamcache_version';
const CACHE_KEY_PREFIX: string = 'steamcache_friend_';
const CACHE_FRIENDSLIST_KEY_PREFIX: string = 'steamcache_friendslist_';
const CACHE_INDEX_KEY: string = 'steamcache_index';
const CACHE_FRIENDSLIST_INDEX_KEY: string = 'steamcache_friendslist_index';
const CACHE_TTL_MS: number = 24 * 60 * 60 * 1000; // 24 hours
const MAX_CACHE_ENTRIES: number = 5000;

interface CacheEntry {
    timestamp: number;
    data: FriendData;
}

interface FriendsListCacheEntry {
    timestamp: number;
    data: Friend[];
}

interface CacheIndexEntry {
    id: SteamID64;
    timestamp: number;
}

// Check cache version and clear if it doesn't match
function checkCacheVersion(): void {
    try {
        const storedVersion: string | null = localStorage.getItem(CACHE_VERSION_KEY);
        const currentVersion: string = String(CACHE_VERSION);
        
        if (storedVersion !== currentVersion) {
            console.log(`Cache version mismatch (stored: ${storedVersion}, current: ${currentVersion}). Clearing cache.`);
            clearCache();
            localStorage.setItem(CACHE_VERSION_KEY, currentVersion);
        }
    } catch (e: unknown) {
        console.warn('Failed to check cache version:', e);
    }
}

// Initialize cache version check on module load
checkCacheVersion();

function getCacheKey(steamId64: SteamID64): string {
    return CACHE_KEY_PREFIX + steamId64;
}

function getCacheIndex(): CacheIndexEntry[] {
    try {
        const indexData: string | null = localStorage.getItem(CACHE_INDEX_KEY);
        if (!indexData) return [];
        return JSON.parse(indexData);
    } catch (e) {
        console.warn('Failed to read cache index:', e);
        return [];
    }
}

function saveCacheIndex(index: CacheIndexEntry[]): void {
    try {
        localStorage.setItem(CACHE_INDEX_KEY, JSON.stringify(index));
    } catch (e: unknown) {
        console.warn('Failed to save cache index:', e);
    }
}

function removeFromIndex(steamId64: SteamID64): void {
    const index: CacheIndexEntry[] = getCacheIndex();
    const filtered: CacheIndexEntry[] = index.filter((entry: CacheIndexEntry): boolean => entry.id !== steamId64);
    saveCacheIndex(filtered);
}

function addToIndex(steamId64: SteamID64, timestamp: number): void {
    const index: CacheIndexEntry[] = getCacheIndex();
    const filtered: CacheIndexEntry[] = index.filter((entry: CacheIndexEntry): boolean => entry.id !== steamId64);
    filtered.push({id: steamId64, timestamp});
    saveCacheIndex(filtered);
}

function evictOldestEntries(count: number): void {
    try {
        const index: CacheIndexEntry[] = getCacheIndex();
        index.sort((a: CacheIndexEntry, b: CacheIndexEntry): number => a.timestamp - b.timestamp);

        const toRemove: CacheIndexEntry[] = index.slice(0, Math.min(count, index.length));
        for (const entry of toRemove) {
            localStorage.removeItem(getCacheKey(entry.id));
        }

        const remaining: CacheIndexEntry[] = index.slice(toRemove.length);
        saveCacheIndex(remaining);
    } catch (e: unknown) {
        console.warn('Failed to evict cache entries:', e);
    }
}

export function setFriend(steamId64: SteamID64, friendData: FriendData): void {
    try {
        const timestamp: number = Date.now();

        const index: CacheIndexEntry[] = getCacheIndex();
        if (index.length >= MAX_CACHE_ENTRIES) {
            const toEvict: number = Math.max(1, Math.floor(MAX_CACHE_ENTRIES * 0.1));
            evictOldestEntries(toEvict);
        }

        const cacheEntry: CacheEntry = {
            timestamp,
            data: friendData,
        };
        localStorage.setItem(getCacheKey(steamId64), JSON.stringify(cacheEntry));
        addToIndex(steamId64, timestamp);
    } catch (e: unknown) {
        console.warn('Failed to cache friend data:', e);
    }
}

export function getFriend(steamId64: SteamID64): FriendData | null {
    try {
        const cached: string | null = localStorage.getItem(getCacheKey(steamId64));
        if (!cached) return null;

        const cacheEntry: CacheEntry = JSON.parse(cached);
        const age: number = Date.now() - cacheEntry.timestamp;

        if (age > CACHE_TTL_MS) {
            localStorage.removeItem(getCacheKey(steamId64));
            removeFromIndex(steamId64);
            return null;
        }

        return cacheEntry.data;
    } catch (e: unknown) {
        console.warn('Failed to read cache:', e);
        return null;
    }
}

export function getFriends(steamIds: SteamID64[]): {
    cached: Record<string, FriendData>;
    missing: SteamID64[];
} {
    const cached: Record<string, FriendData> = {};
    const missing: SteamID64[] = [];

    for (const id of steamIds) {
        const data: FriendData | null = getFriend(id);
        if (data) {
            cached[id] = data;
        } else {
            missing.push(id);
        }
    }

    return {cached, missing};
}

export function clearCache(): number {
    try {
        const index: CacheIndexEntry[] = getCacheIndex();
        for (const entry of index) {
            localStorage.removeItem(getCacheKey(entry.id));
        }
        localStorage.removeItem(CACHE_INDEX_KEY);
        
        // Clear friends list cache
        const friendsListIndex: CacheIndexEntry[] = getFriendsListCacheIndex();
        for (const entry of friendsListIndex) {
            localStorage.removeItem(getFriendsListCacheKey(entry.id));
        }
        localStorage.removeItem(CACHE_FRIENDSLIST_INDEX_KEY);
        
        // Note: We don't remove CACHE_VERSION_KEY here because we want to preserve it
        // The version is only cleared when the cache is being reset due to version mismatch
        return index.length + friendsListIndex.length;
    } catch (e: unknown) {
        console.warn('Failed to clear cache:', e);
        return 0;
    }
}

// Friends list caching functions
function getFriendsListCacheKey(steamId64: SteamID64): string {
    return CACHE_FRIENDSLIST_KEY_PREFIX + steamId64;
}

function getFriendsListCacheIndex(): CacheIndexEntry[] {
    try {
        const indexData: string | null = localStorage.getItem(CACHE_FRIENDSLIST_INDEX_KEY);
        if (!indexData) return [];
        return JSON.parse(indexData);
    } catch (e) {
        console.warn('Failed to read friends list cache index:', e);
        return [];
    }
}

function saveFriendsListCacheIndex(index: CacheIndexEntry[]): void {
    try {
        localStorage.setItem(CACHE_FRIENDSLIST_INDEX_KEY, JSON.stringify(index));
    } catch (e: unknown) {
        console.warn('Failed to save friends list cache index:', e);
    }
}

function removeFromFriendsListIndex(steamId64: SteamID64): void {
    const index: CacheIndexEntry[] = getFriendsListCacheIndex();
    const filtered: CacheIndexEntry[] = index.filter((entry: CacheIndexEntry): boolean => entry.id !== steamId64);
    saveFriendsListCacheIndex(filtered);
}

function addToFriendsListIndex(steamId64: SteamID64, timestamp: number): void {
    const index: CacheIndexEntry[] = getFriendsListCacheIndex();
    const filtered: CacheIndexEntry[] = index.filter((entry: CacheIndexEntry): boolean => entry.id !== steamId64);
    filtered.push({id: steamId64, timestamp});
    saveFriendsListCacheIndex(filtered);
}

function evictOldestFriendsListEntries(count: number): void {
    try {
        const index: CacheIndexEntry[] = getFriendsListCacheIndex();
        index.sort((a: CacheIndexEntry, b: CacheIndexEntry): number => a.timestamp - b.timestamp);

        const toRemove: CacheIndexEntry[] = index.slice(0, Math.min(count, index.length));
        for (const entry of toRemove) {
            localStorage.removeItem(getFriendsListCacheKey(entry.id));
        }

        const remaining: CacheIndexEntry[] = index.slice(toRemove.length);
        saveFriendsListCacheIndex(remaining);
    } catch (e: unknown) {
        console.warn('Failed to evict friends list cache entries:', e);
    }
}

export function setFriendsList(steamId64: SteamID64, friendsList: Friend[]): void {
    try {
        const timestamp: number = Date.now();

        const index: CacheIndexEntry[] = getFriendsListCacheIndex();
        if (index.length >= MAX_CACHE_ENTRIES) {
            const toEvict: number = Math.max(1, Math.floor(MAX_CACHE_ENTRIES * 0.1));
            evictOldestFriendsListEntries(toEvict);
        }

        const cacheEntry: FriendsListCacheEntry = {
            timestamp,
            data: friendsList,
        };
        localStorage.setItem(getFriendsListCacheKey(steamId64), JSON.stringify(cacheEntry));
        addToFriendsListIndex(steamId64, timestamp);
    } catch (e: unknown) {
        console.warn('Failed to cache friends list:', e);
    }
}

export function getFriendsList(steamId64: SteamID64): Friend[] | null {
    try {
        const cached: string | null = localStorage.getItem(getFriendsListCacheKey(steamId64));
        if (!cached) return null;

        const cacheEntry: FriendsListCacheEntry = JSON.parse(cached);
        const age: number = Date.now() - cacheEntry.timestamp;

        if (age > CACHE_TTL_MS) {
            localStorage.removeItem(getFriendsListCacheKey(steamId64));
            removeFromFriendsListIndex(steamId64);
            return null;
        }

        return cacheEntry.data;
    } catch (e: unknown) {
        console.warn('Failed to read friends list cache:', e);
        return null;
    }
}
